import React, { useCallback, useEffect, useState } from 'react';
import {
  FlexGrid,
  Heading,
  KalturaVideoPlayer,
  TextBoldness,
  Typography,
  getClassNameFactory,
  kalturaSetupHandlerThunk,
} from '@dcl/ui';
import fetchApi from '../../../../utils/fetchApi';
import { GET_VIDEO_DATA_API, VIDEO_FORMAT } from '../../utils/constants';
import { TypographyTags } from '@dcl/ui/dist/elements/Typography';
import { VideoIcon } from '../../icons/VideoIcon';
import { convertSecondsIntoMins } from '../../../../utils/convertSecondsIntoMins';
import { boldTypes, types } from '@dcl/ui/dist/elements/Heading';
import { IVideoPlayer } from './types';
import './VideoPlayer.css';

export const VideoPlayer: React.FC<IVideoPlayer> = (props: IVideoPlayer) => {
  const { entry_ID, ks } = props;
  const [videoData, setVideoData] = useState({
    videoTitle: '',
    videoDescription: '',
    videoDuration: '',
    videoViews: '',
  });

  const initiateVideodataApi = useCallback(async () => {
    const response = await fetchApi({
      baseHostUrl: GET_VIDEO_DATA_API,
      options: {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entryId: entry_ID,
          version: -1,
          ks: ks,
          format: 1,
        }),
      },
    });

    const videoMetaData = await response
      .text()
      .then((data) => JSON.parse(data));

    videoMetaData.objectType === 'KalturaMediaEntry' &&
      setVideoData((previousInput) => ({
        ...previousInput,
        videoTitle: videoMetaData.name,
        videoDescription: videoMetaData.description,
        videoDuration: videoMetaData.duration,
        videoViews: videoMetaData.views,
      }));
  }, [entry_ID, ks]);

  useEffect(() => {
    initiateVideodataApi();
  }, [initiateVideodataApi]);

  kalturaSetupHandlerThunk();
  const getClassName = getClassNameFactory(VideoPlayer.displayName);

  return (
    <div className={getClassName()}>
      <FlexGrid>
        <FlexGrid.Row>
          <FlexGrid.Col
            xs={12}
            lg={8}
            sm={12}
            className={getClassName({ descendantName: 'videoPlayerBox' })}
          >
            <KalturaVideoPlayer entryId={entry_ID} ks={ks} />
          </FlexGrid.Col>
        </FlexGrid.Row>

        <>
          <FlexGrid.Row>
            <FlexGrid.Col
              xs={12}
              sm={12}
              md={12}
              className={getClassName({ descendantName: 'videoHeading' })}
            >
              <Heading
                type={types.Medium}
                boldType={boldTypes.regular}
                className={getClassName({ descendantName: 'heading' })}
              >
                {videoData.videoTitle}
              </Heading>
            </FlexGrid.Col>
          </FlexGrid.Row>

          <FlexGrid.Row className={getClassName({ descendantName: 'eyeBrow' })}>
            <FlexGrid.Col xs={3} sm={3} md={3}>
              <Typography
                tag={TypographyTags.SPAN}
                boldness={TextBoldness.Regular}
                className={getClassName({
                  descendantName: 'VideoFormat',
                })}
              >
                {'Format'}:
              </Typography>
            </FlexGrid.Col>

            <FlexGrid.Col xs={9} sm={9} md={9}>
              <div className={getClassName({ descendantName: 'Info' })}>
                <div className={getClassName({ descendantName: 'Icon' })}>
                  <VideoIcon />
                </div>
                <Typography
                  tag={TypographyTags.SPAN}
                  boldness={TextBoldness.Light}
                  className={getClassName({
                    descendantName: 'VideoFormat',
                  })}
                >
                  {VIDEO_FORMAT}
                </Typography>
                <Typography
                  tag={TypographyTags.SPAN}
                  boldness={TextBoldness.Regular}
                >
                  {convertSecondsIntoMins(videoData.videoDuration)}
                </Typography>
                <Typography
                  tag={TypographyTags.SPAN}
                  boldness={TextBoldness.Regular}
                >
                  ·
                </Typography>
                <Typography
                  tag={TypographyTags.SPAN}
                  boldness={TextBoldness.Regular}
                >
                  {videoData.videoViews} views
                </Typography>
              </div>
            </FlexGrid.Col>
          </FlexGrid.Row>
        </>
      </FlexGrid>
    </div>
  );
};

VideoPlayer.displayName = 'VideoPlayer';
